<template>
  <div class="theme-page-container">
    <PremiumPage
      title="Theme Settings"
      subtitle="Customize the look and feel of your admin portal"
      showBack
    >
      <div class="pa-6">
        <v-row>

          <!-- ── Primary Color Picker ── -->
          <v-col cols="6">
            <div class="premium-panel">
              <div class="premium-panel-header">
                <div>
                  <div class="premium-panel-title">Primary Color</div>
                  <div class="premium-panel-sub">
                    Used for sidebar accent, active states, and highlights
                  </div>
                </div>
                <div
                  class="color-dot"
                  :style="`background:${theme_primary_color || primaryColor}`"
                ></div>
              </div>

              <!-- Selected color preview strip -->
              <!-- <div class="color-preview-strip" :style="`background:${theme_primary_color || primaryColor}`">
                <span class="color-preview-hex">{{ theme_primary_color || primaryColor }}</span>
              </div> -->

              <div class="premium-panel-body">
                <v-color-picker
                  v-model="theme_primary_color"
                  dot-size="22"
                  hide-canvas
                  show-swatches
                  swatches-max-height="180"
                  mode="hexa"
                  hide-mode-switch
                  flat
                  width="100%"
                />
              </div>
            </div>
          </v-col>

          <!-- ── Button Color Picker ── -->
          <v-col cols="6">
            <div class="premium-panel">
              <div class="premium-panel-header">
                <div>
                  <div class="premium-panel-title">Button Color</div>
                  <div class="premium-panel-sub">
                    Used for action buttons and secondary elements
                  </div>
                </div>
                <div
                  class="color-dot"
                  :style="`background:${theme_button_color || buttonColor}`"
                ></div>
              </div>

              <!-- Selected color preview strip -->
              <!-- <div class="color-preview-strip" :style="`background:${theme_button_color || buttonColor}`">
                <span class="color-preview-hex">{{ theme_button_color || buttonColor }}</span>
              </div> -->

              <div class="premium-panel-body">
                <v-color-picker
                  v-model="theme_button_color"
                  dot-size="22"
                  hide-canvas
                  show-swatches
                  swatches-max-height="180"
                  mode="hexa"
                  hide-mode-switch
                  flat
                  width="100%"
                />
              </div>
            </div>
          </v-col>

          <!-- ── Save Button ── -->
        </v-row>
      </div>

      <template #footer>
        <v-btn text class="btn-premium-cancel" @click="resetColors">
          Reset to Default
        </v-btn>
        <v-btn
          depressed
          :loading="loading"
          class="btn-premium-primary ml-3"
          @click="updateTheme"
        >
          Submit
        </v-btn>
      </template>
    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumPage from "../common/PremiumPage.vue";

export default {
  components: {
    PremiumPage,
  },
  data() {
    return {
      loading: false,
      theme_primary_color: "",
      theme_button_color: "",
      serverSideErrors: [],
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),
    ...mapMutations("theme", [
      "setDarkMode",
      "setPrimaryColor",
      "setButtonColor",
    ]),

    resetColors() {
      this.theme_primary_color = "#f87676";
      this.theme_button_color = "#30384d";
    },

    updateTheme() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.loading = true;

          this.$axios
            .put("/societyadmin/theme", {
              theme_primary_color: this.theme_primary_color,
              theme_button_color: this.theme_button_color,
            })
            .then((response) => {
              const data = response.data;
              this.loading = false;
              this.serverSideErrors = {};

              if (data.status) {
                // Persist to localStorage
                let user = JSON.parse(localStorage.getItem("user") || "{}");
                user.theme_primary_color = this.theme_primary_color;
                user.theme_button_color = this.theme_button_color;
                localStorage.setItem("user", JSON.stringify(user));

                // Apply to Vuetify
                this.$vuetify.theme.themes.light.primary =
                  this.theme_primary_color;
                this.$vuetify.theme.themes.dark.primary =
                  this.theme_primary_color;
                this.$vuetify.theme.themes.light.secondary =
                  this.theme_button_color;
                this.$vuetify.theme.themes.dark.secondary =
                  this.theme_button_color;

                // Apply to Vuex + CSS var
                this.setPrimaryColor({
                  primaryColor: this.theme_primary_color,
                });
                this.setButtonColor({ buttonColor: this.theme_button_color });
                document.documentElement.style.setProperty(
                  "--brand",
                  this.theme_primary_color,
                );
                document.documentElement.style.setProperty(
                  "--btn-color",
                  this.theme_button_color,
                );

                this.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "green accent-4",
                  timeout: 2000,
                });
              } else {
                if (!data.errors) {
                  this.setAlert({
                    showAlert: true,
                    message: data.message,
                    variant: "red accent-3",
                    timeout: 2000,
                  });
                } else {
                  for (let i = 0; i < data.errors.length; i++) {
                    this.serverSideErrors[data.errors[i].field] =
                      data.errors[i].message;
                  }
                }
              }
            });
        }
      });
    },
  },

  mounted() {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    this.theme_primary_color =
      user.theme_primary_color || this.primaryColor || "#f87676";
    this.theme_button_color =
      user.theme_button_color || this.buttonColor || "#30384d";
  },
};
</script>

<style scoped>
.theme-page-container {
  padding: 24px;
}
/* ── Mode toggle ── */
.mode-toggle-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.mode-option {
  border: 2px solid var(--border, #e5e7eb);
  border-radius: 14px;
  padding: 14px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.mode-option:hover {
  border-color: rgba(248, 118, 118, 0.6);
}

.mode-preview {
  border-radius: 8px;
  overflow: hidden;
  height: 80px;
  display: flex;
  margin-bottom: 10px;
  border: 1px solid var(--border, #e5e7eb);
}

/* Light preview */
.light-preview {
  background: #edf0f4;
}
.mp-sidebar {
  width: 28px;
  background: #1a1f2e;
  flex-shrink: 0;
}
.mp-content {
  flex: 1;
  padding: 6px;
}
.mp-bar {
  height: 10px;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 6px;
}
.mp-card {
  height: 20px;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 4px;
}
.mp-card.short {
  height: 14px;
  width: 75%;
}

/* Dark preview */
.dark-preview {
  background: #080e1b;
}
.dark-side {
  background: #050a14;
}
.dark-content {
  background: #080e1b;
}
.dark-bar {
  background: #111827;
}
.dark-card {
  background: #162035;
}

.mode-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx2, #374151);
}

.mode-check {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Color swatches ── */
.swatch-row {
  display: flex;
  gap: 16px;
}
.swatch-item {
  flex: 1;
  text-align: center;
}
.swatch-color {
  height: 40px;
  border-radius: 8px;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transition: transform 0.2s;
}
.swatch-color:hover {
  transform: scale(1.03);
}
.swatch-label {
  font-size: 11.5px;
  color: var(--tx3);
  font-weight: 500;
}
.swatch-hex {
  font-size: 12px;
  color: var(--tx2);
  margin-top: 2px;
}

/* ── Live preview bar ── */
.preview-bar {
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 8px;
  overflow: hidden;
  height: 48px;
  display: flex;
}
.preview-sidebar {
  width: 36px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 0;
}
.preview-dot {
  width: 16px;
  height: 3px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 2px;
}
.preview-dot.short {
  width: 10px;
}
.preview-main {
  flex: 1;
  background: var(--surface2, #f9fafb);
}
.preview-topbar {
  height: 22px;
  background: var(--surface, #fff);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 8px;
}
.preview-avatar {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  opacity: 0.85;
}
.preview-btn {
  height: 14px;
  width: 60px;
  border-radius: 4px;
  margin: 8px 10px;
  opacity: 0.85;
}

/* ── Premium Panels ── */
.premium-panel {
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.premium-panel-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border, #e5e7eb);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--surface, #ffffff);
}

.premium-panel-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--tx, #111827);
  letter-spacing: -0.01em;
}

.premium-panel-sub {
  font-size: 12px;
  color: var(--tx2, #6b7280);
  margin-top: 2px;
}

.premium-panel-body {
  padding: 20px;
  flex: 1;
}

/* Color preview strip */
.color-preview-strip {
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
}

.color-preview-hex {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.12em;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  background: rgba(0, 0, 0, 0.15);
  padding: 3px 12px;
  border-radius: 20px;
}

/* Color dot in panel header */
.color-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

/* ── v-color-picker deep overrides ── */

/* Remove default picker elevation/shadow */
::v-deep .v-color-picker {
  box-shadow: none !important;
  background: transparent !important;
  width: 100% !important;
}

/* Hue slider track */
::v-deep .v-color-picker__sliders {
  padding: 0 0 8px 0 !important;
}

::v-deep .v-slider__track-container {
  height: 10px !important;
  border-radius: 8px !important;
  overflow: hidden;
}

::v-deep .v-slider__track-background,
::v-deep .v-slider__track-fill {
  border-radius: 8px !important;
}

/* Slider thumb */
::v-deep .v-slider__thumb {
  width: 18px !important;
  height: 18px !important;
  left: -9px !important;
  border: 2.5px solid #fff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25) !important;
  cursor: pointer;
}

::v-deep .v-slider__thumb::before {
  opacity: 0 !important;
}

/* HEX input field */
::v-deep .v-color-picker__input input {
  border: 1.5px solid var(--border, #e2e8f0) !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  font-family: 'Courier New', monospace !important;
  padding: 6px 10px !important;
  height: 36px !important;
  text-align: center;
  background: var(--surface2, #f9fafb) !important;
  color: var(--tx, #111827) !important;
  box-shadow: none !important;
  transition: border-color 0.2s;
}

::v-deep .v-color-picker__input input:focus {
  border-color: var(--brand, #0d9972) !important;
  outline: none;
}

::v-deep .v-color-picker__input span {
  font-size: 10px !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em !important;
  color: var(--tx3, #9ca3af) !important;
  text-transform: uppercase;
  margin-top: 4px !important;
}

/* Swatches */
::v-deep .v-color-picker__swatch {
  border-radius: 6px !important;
  overflow: hidden;
}

::v-deep .v-color-picker__color {
  border-radius: 4px !important;
  height: 18px !important;
  width: 26px !important;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

::v-deep .v-color-picker__color:hover {
  transform: scale(1.15) !important;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2) !important;
  z-index: 1;
  position: relative;
}
</style>
